import { useState } from 'react';
import { Controller } from 'react-hook-form';
import {
  InputText,
  InputSize,
  DateInput,
  PickerPosition,
  Select,
  SelectSize,
  DateInputSize,
  Autocomplete,
  PermissionEnum,
  IconUp,
  IconDown,
} from '@ui/src';
import CreateFormRow from '../../common/create-form-row/create-form-row';
import { ApplicationScheme } from '../../../hoc/common/types';
import { IndividualCustomerCreateFormTypes } from '../types';

import cn from '../../common/styles.module.css';

const IndividualCustomerCreateForm = ({
  control,
  errors,
  sumSubError,
  applicationSchemes,
  activePermissions,
  setSelectedSchema,
  deliveryChannelsIndividual,
  countries,
}: IndividualCustomerCreateFormTypes) => {
  const [applicationSchemeVisible, setApplicationSchemeVisible] =
    useState(false);

  return (
    <div className={cn.form}>
      <div className={cn.formTitle}>Contacts</div>
      <CreateFormRow title="First name" id="firstName">
        <Controller
          control={control}
          name="firstName"
          defaultValue=""
          render={({ field: { onChange, value } }) => (
            <InputText
              name="firstName"
              className={`borderless ${
                errors?.firstName?.message ? cn.inputError : ''
              }`}
              placeholder="Enter First Name"
              onChange={onChange}
              value={value}
              error={errors?.firstName?.message}
              size={InputSize.SMALL}
              tabIndex={0}
            />
          )}
        />
      </CreateFormRow>
      <CreateFormRow title="Last name" id="lastName">
        <Controller
          control={control}
          name="lastName"
          defaultValue=""
          render={({ field: { onChange, value } }) => (
            <InputText
              name="lastName"
              className={`borderless ${
                errors?.lastName?.message ? cn.inputError : ''
              }`}
              placeholder="Enter Last Name"
              onChange={onChange}
              value={value}
              error={errors?.lastName?.message}
              size={InputSize.SMALL}
              tabIndex={0}
            />
          )}
        />
      </CreateFormRow>
      <CreateFormRow title="Phone number" id="phone">
        <Controller
          control={control}
          name="phone"
          defaultValue=""
          render={({ field: { onChange, value } }) => (
            <InputText
              name="phone"
              className={`borderless ${
                errors?.phone?.message ? cn.inputError : ''
              }`}
              placeholder="Enter Phone"
              onChange={onChange}
              value={value}
              error={errors?.phone?.message}
              size={InputSize.SMALL}
              tabIndex={0}
            />
          )}
        />
      </CreateFormRow>
      <CreateFormRow title="E-mail" id="email">
        <Controller
          control={control}
          name="email"
          defaultValue=""
          render={({ field: { onChange, value } }) => (
            <InputText
              name="email"
              className={`borderless ${
                errors?.email?.message ? cn.inputError : ''
              }`}
              placeholder="Enter Email"
              onChange={onChange}
              value={value}
              error={errors?.email?.message}
              size={InputSize.SMALL}
              tabIndex={0}
            />
          )}
        />
      </CreateFormRow>
      <CreateFormRow title="Date of Birth" id="dob">
        <Controller
          control={control}
          name="dob"
          defaultValue=""
          render={({ field: { onChange, value } }) => (
            <DateInput
              value={value}
              onChange={(date) => onChange(date.toISOString())}
              placeholder="Select Date Of Birth"
              error={errors?.dob?.message}
              className={errors?.dob?.message ? cn.dateInputError : ''}
              size={DateInputSize.SMALL}
              pickerPisition={PickerPosition.RIGHT}
              pickerClassName={cn.dateOfBirthPicker}
              tabIndex={0}
              birthdayPicker
              borderless
              withoutCustomSelector
            />
          )}
        />
      </CreateFormRow>
      <CreateFormRow title="Nationality" id="nationalityId">
        <Controller
          control={control}
          name="nationalityId"
          defaultValue=""
          render={({ field: { onChange, value } }) => (
            <Autocomplete
              name="nationalityId"
              placeholder="Select country..."
              list={countries || []}
              value={value}
              size={SelectSize.SMALL}
              className={errors?.nationalityId?.message ? cn.selectError : ''}
              onChange={onChange}
              error={errors?.nationalityId?.message}
              tabIndex={0}
              withClearButton
              borderless
            />
          )}
        />
      </CreateFormRow>
      <CreateFormRow title="US Citizen" id="isUSResident">
        <Controller
          control={control}
          name="isUSResident"
          render={({ field: { onChange, value } }) => (
            <Select
              name="US Citizen"
              placeholder="Select option..."
              list={[
                { name: 'Yes', value: 'yes' },
                { name: 'No', value: 'no' },
              ]}
              value={value ? 'yes' : 'no'}
              size={SelectSize.SMALL}
              onChange={(e) => onChange(Boolean(e.target.value === 'yes'))}
              tabIndex={0}
              borderless
            />
          )}
        />
      </CreateFormRow>
      <CreateFormRow
        title="Participation in politics"
        id="participatesInPolitics"
      >
        <Controller
          control={control}
          name="participatesInPolitics"
          render={({ field: { onChange, value } }) => (
            <Select
              name="participatesInPolitics"
              placeholder="Select option"
              list={[
                { name: 'Yes, I am participating', value: 'yes' },
                { name: 'Not participating', value: 'no' },
              ]}
              size={SelectSize.SMALL}
              value={value ? 'yes' : 'no'}
              onChange={(e) => onChange(Boolean(e.target.value === 'yes'))}
              tabIndex={0}
              borderless
            />
          )}
        />
      </CreateFormRow>
      <CreateFormRow title="SumSub application ID" id="applicantId">
        <Controller
          control={control}
          name="applicantId"
          defaultValue=""
          render={({ field: { onChange, value } }) => (
            <InputText
              name="applicantId"
              placeholder="Enter SumSub Application ID"
              value={value}
              onChange={onChange}
              size={InputSize.SMALL}
              className={`borderless ${
                sumSubError || errors?.applicantId?.message ? cn.inputError : ''
              }`}
              tabIndex={0}
              error={sumSubError || errors?.applicantId?.message}
            />
          )}
        />
      </CreateFormRow>
      <CreateFormRow title="Delivery channel" id="deliveryChannelId">
        <Controller
          control={control}
          name="deliveryChannelId"
          render={({ field: { onChange, value } }) => (
            <Select
              name="Delivery channel"
              placeholder="Select option..."
              list={deliveryChannelsIndividual}
              value={value}
              size={SelectSize.SMALL}
              onChange={onChange}
              error={errors?.deliveryChannelId?.message}
              tabIndex={0}
              withClearButton
              borderless
            />
          )}
        />
      </CreateFormRow>
      <button
        type="button"
        className={`${cn.formTitle} ${cn.formTitleButton}`}
        onClick={() => setApplicationSchemeVisible(!applicationSchemeVisible)}
      >
        {applicationSchemeVisible ? <IconUp /> : <IconDown />}
        Application Scheme
      </button>
      {applicationSchemeVisible &&
        activePermissions?.includes(
          PermissionEnum.APPLICATIONS_SCHEMES_LIST,
        ) && (
          <CreateFormRow title="Individual scheme" id="applicationSchemeId">
            <Controller
              control={control}
              name="applicationSchemeId"
              defaultValue=""
              render={({ field: { onChange, value } }) => (
                <Select
                  name="applicationSchemeId"
                  placeholder="Select an option"
                  list={applicationSchemes?.map((item) => ({
                    name: item.name,
                    value: item.id,
                  }))}
                  value={value}
                  size={SelectSize.SMALL}
                  onChange={(e) => {
                    onChange(e);
                    setSelectedSchema(
                      applicationSchemes?.find(
                        (item) => item.id === e.target.value,
                      ) as ApplicationScheme,
                    );
                  }}
                  tabIndex={0}
                  error={errors?.applicationSchemeId?.message}
                  withClearButton
                  borderless
                />
              )}
            />
          </CreateFormRow>
        )}
      {applicationSchemeVisible &&
        !activePermissions?.includes(
          PermissionEnum.APPLICATIONS_SCHEMES_LIST,
        ) && (
          <div className={cn.applicationSchemeDenied}>
            You don`t have access to this section
          </div>
        )}
    </div>
  );
};

export default IndividualCustomerCreateForm;
